import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useMyGitchaReach, { MY_GITCHA_REACH_KEY } from "@/hooks/useMyGitchaReach";
import { resendLeadForward, updateMyAgentPage } from "@/services/gitchaReachService";
import "./CrmForwardSetting.scss";

const errorOf = (error) =>
  error?.response?.data?.data?.errors?.leadForwardEmail ||
  error?.response?.data?.result?.errors?.leadForwardEmail ||
  error?.response?.data?.message ||
  "That didn't save. Check your connection and try again.";

const CrmForwardSetting = () => {
  const queryClient = useQueryClient();
  const page = useMyGitchaReach();
  const saved = page.data?.leadForwardEmail || "";
  const pending = page.data?.leadForwardPending || "";
  const [value, setValue] = useState(saved);
  const [problem, setProblem] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    setValue(saved);
  }, [saved]);

  const call = (request) => async (arg) => {
    const response = await request(arg);
    if (response?.errorCode && response.errorCode !== 0) {
      const error = new Error(response?.message);
      error.response = { data: response };
      throw error;
    }
    return response?.result || null;
  };

  const resend = useMutation({
    mutationFn: call(() => resendLeadForward()),
    onSuccess: (result) => {
      if (result) queryClient.setQueryData(MY_GITCHA_REACH_KEY, result);
      setProblem("");
      setDone(`Sent a new link to ${pending}.`);
    },
    onError: (error) => {
      setDone("");
      setProblem(errorOf(error));
    },
  });

  const save = useMutation({
    mutationFn: async (leadForwardEmail) => {
      const response = await updateMyAgentPage({ leadForwardEmail });
      if (response?.errorCode && response.errorCode !== 0) {
        const error = new Error(response?.message);
        error.response = { data: response };
        throw error;
      }
      return response?.result || null;
    },
    onSuccess: (result, leadForwardEmail) => {
      if (result) queryClient.setQueryData(MY_GITCHA_REACH_KEY, result);
      setProblem("");
      setDone(leadForwardEmail ? "" : "Removed. Contacts go to your email only.");
    },
    onError: (error) => {
      setDone("");
      setProblem(errorOf(error));
    },
  });

  const submit = (event) => {
    event.preventDefault();
    save.mutate(value.trim());
  };

  // An API without lead forwarding drops the field, so saving would look like it worked and do nothing.
  const supported = page.data && ("leadForwardEmail" in page.data || "leadForwardPending" in page.data);
  if (page.isError || (!page.isLoading && !supported)) return null;

  return (
    <div className="notification-group crm-forward">
      <form className="crm-forward__form" onSubmit={submit} noValidate>
        <label htmlFor="crm-forward-email">Copy every Gitcha contact to your CRM</label>
        <div className="crm-forward__row">
          <input
            id="crm-forward-email"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="Your CRM's lead email address"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setProblem("");
              setDone("");
            }}
            disabled={page.isLoading}
            aria-invalid={Boolean(problem)}
          />
          <button type="submit" className="btn btn-primary crm-forward__save" disabled={save.isPending || page.isLoading}>
            {save.isPending ? "Saving..." : "Save"}
          </button>
        </div>
        {problem ? (
          <p className="crm-forward__error" role="alert">
            {problem}
          </p>
        ) : null}
        {done ? (
          <p className="crm-forward__done" role="status">
            {done}
          </p>
        ) : null}
        {pending && !problem ? (
          <p className="crm-forward__state" role="status">
            Check {pending} for a confirmation link.{" "}
            <button type="button" className="crm-forward__link" onClick={() => resend.mutate()} disabled={resend.isPending}>
              {resend.isPending ? "Sending..." : "Resend"}
            </button>
          </p>
        ) : null}
        {saved && !pending && !done ? (
          <p className="crm-forward__state">
            Forwarding to {saved}.{" "}
            <button type="button" className="crm-forward__link" onClick={() => save.mutate("")}>
              Stop
            </button>
          </p>
        ) : null}
      </form>
    </div>
  );
};

export default CrmForwardSetting;
